import type { RequirementFlagDetail } from '@repo/sdk/gen'
import { requirementFlagStartReview } from '@repo/sdk/gen'
import { Badge } from '@repo/ui/badge'
import { makeBreadcrumbFromCache } from '@repo/ui/components/query-breadcrumbs'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import i18n, { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { WhenWritable } from '../../../-components/when-writable'
import {
	FLAG_CODE_LABEL,
	FLAG_STATE_LABEL,
	FLAG_STATE_TONE
} from '../../-lib/flag-labels'
import { flagDetailQueryOptions } from '../-query/query-options'
import { FlagAnswer } from './-components/flag-answer'
import { FlagClosed } from './-components/flag-closed'
import { FlagDocument } from './-components/flag-document'
import { FlagFacts } from './-components/flag-facts'
import { FlagOutcomeForm } from './-components/flag-outcome-form'
import { FlagPecReply } from './-components/flag-pec-reply'

export const Route = createFileRoute(
	'/(private)/bando/$residenceId/segnalazioni/$flagId/'
)({
	// The loader reads a student's data and logs the access: only opening the
	// page runs it, never a hover on a link.
	preload: false,
	loader: async ({ context, params }) => {
		try {
			await context.queryClient.ensureQueryData({
				...flagDetailQueryOptions(params.residenceId, params.flagId),
				revalidateIfStale: true
			})
		} catch (error) {
			if (
				typeof error === 'object' &&
				error !== null &&
				'code' in error &&
				(error.code === 'requirement_flag_not_found' ||
					error.code === 'residence_not_found')
			) {
				throw notFound()
			}
			throw error
		}
	},
	staticData: {
		breadcrumb: ({ params, queryClient }) =>
			makeBreadcrumbFromCache<RequirementFlagDetail>(
				queryClient,
				makeQueryKeys.flags.detail(
					params.residenceId ?? '',
					params.flagId ?? ''
				),
				(detail) =>
					`${i18n.t(FLAG_CODE_LABEL[detail.flag.code])} · ${detail.flag.protocol}`
			)
	},
	component: FlagCardPage
})

/**
 * One flag (FFD 2.6, steps 2 to 5): the value declared beside the threshold,
 * the document that proves it beside them, and the outcome. The page of the
 * document on screen is the page the outcome cites.
 */
function FlagCardPage() {
	const { t } = useTranslation()
	const { residenceId, flagId } = Route.useParams()
	const { data } = useSuspenseQuery(
		flagDetailQueryOptions(residenceId, flagId)
	)
	const { flag, files } = data
	const [fileId, setFileId] = useState(
		() => (files.find((file) => file.relevant) ?? files[0])?.id ?? null
	)
	const [page, setPage] = useState<number | null>(null)
	const closed =
		flag.state === 'resolved_confirmed' ||
		flag.state === 'resolved_corrected'

	const startReview = useMutation({
		mutationFn: () =>
			requirementFlagStartReview({
				body: { data: { residenceId, flagId } }
			})
	})
	const { mutate } = startReview
	// "In esame da": opening the card tells the other managers, once.
	useEffect(() => {
		if (!closed) {
			mutate()
		}
	}, [closed, mutate])

	return (
		<main className='flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 md:p-6'>
			<header className='flex flex-col gap-2'>
				<div className='flex flex-wrap items-center gap-3'>
					<h1 className='font-semibold text-2xl'>
						{t(FLAG_CODE_LABEL[flag.code])}
					</h1>
					<Badge variant={FLAG_STATE_TONE[flag.state]}>
						{t(FLAG_STATE_LABEL[flag.state])}
					</Badge>
				</div>
				{flag.reviewing && !closed && (
					<p className='text-muted-foreground text-sm'>
						{t('admin-flags:reviewing', {
							name:
								flag.reviewing.name ?? t('admin-flags:someone')
						})}
					</p>
				)}
			</header>
			<div className='grid min-w-0 gap-6 lg:grid-cols-2'>
				<div className='flex min-w-0 flex-col gap-6'>
					{data.pecReply && <FlagPecReply reply={data.pecReply} />}
					<FlagFacts detail={data} residenceId={residenceId} />
					{flag.clarification?.answer && (
						<FlagAnswer
							answer={flag.clarification.answer}
							flagId={flag.id}
							residenceId={residenceId}
						/>
					)}
					{closed ? (
						<FlagClosed detail={data} />
					) : (
						<WhenWritable>
							<FlagOutcomeForm
								detail={data}
								fileId={fileId}
								page={page}
								residenceId={residenceId}
							/>
						</WhenWritable>
					)}
				</div>
				<FlagDocument
					fileId={fileId}
					files={files}
					onFileChange={(next) => {
						setFileId(next)
						setPage(null)
					}}
					onPageChange={setPage}
					page={page}
					readOnly={closed}
					residenceId={residenceId}
				/>
			</div>
		</main>
	)
}
